import { useCallback, useEffect, useState } from 'react';

import type { AdminUserDto, ApiClient } from '../api';
import { errorMessage } from './format';

type AdminPanelProps = {
  api: Pick<ApiClient, 'adminUsers' | 'approveUser' | 'rejectUser'>;
  /** Refreshes the pending badge in the account menu after a decision. */
  onChanged?(): void;
};

/**
 * The approval queue. Owner-only — App refuses to render it for anyone else, and the
 * API refuses to answer them regardless.
 */
export function AdminPanel({ api, onChanged }: AdminPanelProps) {
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  /** Re-read after a decision. The initial load is the effect below. */
  const reload = useCallback(async () => {
    try {
      setUsers(await api.adminUsers());
      setError(null);
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }, [api]);

  useEffect(() => {
    // Guarded so a slow response cannot set state after the panel has gone.
    let active = true;
    void api.adminUsers().then(
      (loaded) => active && setUsers(loaded),
      (reason: unknown) => active && setError(errorMessage(reason)),
    );
    return () => {
      active = false;
    };
  }, [api]);

  async function decide(id: string, decision: 'approve' | 'reject') {
    setBusyId(id);
    try {
      if (decision === 'approve') await api.approveUser(id);
      else await api.rejectUser(id);
      await reload();
      onChanged?.();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusyId(null);
    }
  }

  if (error) {
    return (
      <section className="admin">
        <h1>Accounts</h1>
        <p className="form-error" role="alert">
          {error}
        </p>
      </section>
    );
  }

  if (!users) {
    return (
      <section className="admin">
        <h1>Accounts</h1>
        <p className="muted">Loading accounts…</p>
      </section>
    );
  }

  const pending = users.filter((user) => user.approvalStatus === 'pending');
  const rest = users.filter((user) => user.approvalStatus !== 'pending');

  return (
    <section className="admin">
      <h1>Accounts</h1>

      <h2>Waiting</h2>
      {pending.length === 0 ? (
        <p className="muted">Nobody is waiting.</p>
      ) : (
        <ul className="admin-list">
          {pending.map((user) => (
            <li key={user.id}>
              <div className="admin-who">
                <strong>{user.displayUsername ?? user.username}</strong>
                <span>{user.email}</span>
                <time dateTime={user.createdAt}>
                  {new Date(user.createdAt).toLocaleDateString()}
                </time>
              </div>
              <div className="admin-actions">
                <button
                  type="button"
                  className="button primary"
                  disabled={busyId === user.id}
                  onClick={() => void decide(user.id, 'approve')}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={busyId === user.id}
                  onClick={() => void decide(user.id, 'reject')}
                >
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2>Everyone else</h2>
      {rest.length === 0 ? (
        <p className="muted">No accounts yet.</p>
      ) : (
        <ul className="admin-list">
          {rest.map((user) => (
            <li key={user.id}>
              <div className="admin-who">
                <strong>{user.displayUsername ?? user.username}</strong>
                <span>{user.email}</span>
                <span>
                  {user.trackCount} {user.trackCount === 1 ? 'set' : 'sets'}
                </span>
              </div>
              <span className={`status ${user.approvalStatus}`}>{user.approvalStatus}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
